import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TOKEN_KEY = 'zamanat_access_token';

/**
 * Questionnaire API Client
 * Handles all API calls for questionnaire management
 */

/**
 * Get full questionnaire with all sections and responses
 * @param {string} leadId - Lead ID
 * @returns {Promise} Questionnaire data
 */
export const getQuestionnaire = async (leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`
      }
    }
  );
  return response.data;
};

/**
 * Update a single questionnaire response
 * @param {string} leadId - Lead ID
 * @param {string} questionId - Question ID
 * @param {object} data - Update data {response_text, change_reason}
 * @returns {Promise} Updated response
 */
export const updateResponse = async (leadId, questionId, data) => {
  const response = await axios.patch(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/responses/${questionId}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

/**
 * Get version history for a response
 * @param {string} leadId - Lead ID
 * @param {string} questionId - Question ID
 * @returns {Promise} Version history
 */
export const getResponseHistory = async (leadId, questionId) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/responses/${questionId}/history`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`
      }
    }
  );
  return response.data;
};

/**
 * Mark questionnaire as complete
 * @param {string} leadId - Lead ID
 * @param {object} data - Completion data {force_complete, notes}
 * @returns {Promise} Completion result
 */
export const markComplete = async (leadId, data = {}) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/complete`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

/**
 * Get questionnaire completion status
 * @param {string} leadId - Lead ID
 * @returns {Promise} Completion status
 */
export const getCompletionStatus = async (leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/status`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`
      }
    }
  );
  return response.data;
};

/**
 * Approve all reviews - clear needs_review flags on all questions
 * @param {string} leadId - Lead ID
 * @returns {Promise} Approval result
 */
export const approveAllReviews = async (leadId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/approve-all-reviews`,
    {},
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

/**
 * Reopen a completed questionnaire so it can receive updates again
 * @param {string} leadId - Lead ID
 * @returns {Promise} Reopen result
 */
export const reopenQuestionnaire = async (leadId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/reopen`,
    {},
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
  'Content-Type': 'application/json',
});

/**
 * Manually trigger questionnaire regeneration
 * @param {string} leadId - Lead ID
 * @param {object} data - Regeneration options
 * @returns {Promise} Regeneration result
 */
export const regenerateQuestionnaire = async (leadId, data = {}) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/regenerate`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

// ─── Change Proposals ───────────────────────────────────────────────────────

export const getChangeProposals = async (leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/proposals`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const getChangeProposalCount = async (leadId) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/proposals/count`,
    { headers: authHeaders() }
  );
  return response.data;
};

export const approveProposal = async (leadId, proposalId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/proposals/${proposalId}/approve`,
    {},
    { headers: authHeaders() }
  );
  return response.data;
};

export const rejectProposal = async (leadId, proposalId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/proposals/${proposalId}/reject`,
    {},
    { headers: authHeaders() }
  );
  return response.data;
};

export const bulkApproveProposals = async (leadId, proposalIds) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/proposals/bulk-approve`,
    { proposal_ids: proposalIds },
    { headers: authHeaders() }
  );
  return response.data;
};

export const bulkRejectProposals = async (leadId, proposalIds) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/leads/${leadId}/questionnaire/proposals/bulk-reject`,
    { proposal_ids: proposalIds },
    { headers: authHeaders() }
  );
  return response.data;
};
